import { Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

/**
 * Manages body-level help tooltips that escape Handsontable's
 * overflow:hidden clipping ancestors. Single instance app-wide so only
 * one tooltip is on screen at a time.
 *
 * Usage:
 *   - From a Handsontable header hook: service.attachToHeader(th)
 *   - On component destroy: service.hide()
 *
 * Icons must carry data-pb-tooltip with the help text and class pb-help-icon.
 */
@Injectable({ providedIn: 'root' })
export class HelpTooltipService implements OnDestroy {

  protected activeTooltip: HTMLDivElement | null = null;
  protected destroy$ = new Subject<void>();

  constructor(protected router: Router) {

    // Auto-hide on navigation so tooltips don't linger across routes.
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
      takeUntil(this.destroy$)
    ).subscribe(() => this.hide());
  }

  ngOnDestroy(): void {

    this.hide();

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Shows a tooltip near the element, reading text from data-pb-tooltip.
   * No-op if the element has no tooltip text. Delegates to showText.
   */
  public show(icon: HTMLElement): void {

    const text = icon.getAttribute('data-pb-tooltip');
    if (!text) return;

    this.showText(icon, text);
  }

  /**
   * Shows a tooltip with the supplied text, anchored above the given
   * element. Text is rendered via textContent — newlines render as line
   * breaks via the .pb-help-tooltip white-space rule. No-op if text is empty.
   */
  public showText(anchor: HTMLElement, text: string): void {

    if (!text) return;

    this.hide();

    const tip = document.createElement('div');
    tip.className = 'pb-help-tooltip';
    tip.textContent = text;
    document.body.appendChild(tip);

    const rect = anchor.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();

    let left = rect.left + (rect.width / 2) - (tipRect.width / 2);
    left = Math.max(8, Math.min(left, window.innerWidth - tipRect.width - 8));

    // Prefer above; flip below when there isn't room above the anchor.
    const spaceAbove = rect.top;
    const fitsAbove = spaceAbove >= tipRect.height + 8;

    const top = fitsAbove
      ? rect.top - tipRect.height - 8
      : rect.bottom + 8;

    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;

    this.activeTooltip = tip;

    // Flip the arrow to match which side the tooltip landed on.
    tip.classList.toggle('pb-help-tooltip--below', !fitsAbove);
  }

  /**
   * Removes the active tooltip if present. Safe to call when nothing
   * is showing.
   */
  public hide(): void {

    if (this.activeTooltip) {
      this.activeTooltip.remove();
      this.activeTooltip = null;
    }
  }

  /**
   * Convenience for Handsontable afterGetColHeader hooks. Finds a
   * pb-help-icon inside the supplied th and binds hover handlers.
   * Idempotent via a sentinel attribute — safe across re-renders.
   */
  public attachToHeader(th: HTMLElement): void {

    const icon = th.querySelector<HTMLElement>('.pb-help-icon[data-pb-tooltip]');

    if (!icon)
      return;

    if (icon.dataset['pbTooltipBound'] === '1')
      return;

    icon.dataset['pbTooltipBound'] = '1';

    icon.addEventListener('mouseenter', () => this.show(icon));
    icon.addEventListener('mouseleave', () => this.hide());
  }

  /**
   * Attach to container
   * @param container
   * @returns
   */
  public attachToContainer(container: HTMLElement): void {

    const icon = container.querySelector<HTMLElement>('.pb-help-icon[data-pb-tooltip]');

    if (!icon)
      return;

    if (icon.dataset['pbTooltipBound'] === '1')
      return;

    icon.dataset['pbTooltipBound'] = '1';

    icon.addEventListener('mouseenter', () => this.show(icon));
    icon.addEventListener('mouseleave', () => this.hide());
  }
}
